import { noSuperjsonTrpcAny } from '@trashlab/api/src/library/frontend/utils/trpc';
import {
  UseTRPCInfiniteQueryOptions,
  UseTRPCInfiniteQueryResult,
  UseTRPCQueryResult,
} from '@trpc/react-query/shared';
import { DefaultErrorShape } from '@trpc/server';
import type { Decimal } from 'decimal.js';
import { ZodIssue } from 'zod';

type ErrorShape = DefaultErrorShape & {
  shape: 'zod';
  data: DefaultErrorShape['data'] & {
    metadata?: Record<string, string>;
    zodError?: ZodIssue[];
  };
};

type Nil = { _nil: true };

export type NoSuperjsonTransportValue<T> = T extends Date
  ? T | string
  : T extends Decimal
  ? T | string
  : T extends bigint
  ? T | string
  : T extends object
  ? { [K in keyof T]: NoSuperjsonTransportValue<T[K]> }
  : T;

type Options<T = any> = {
  trpc?: { abortOnUnmount?: boolean };
  enabled?: boolean;
  suspense?: boolean;
  retry?: ((failureCount: number, error: any) => boolean) | boolean | number;
  staleTime?: number;
  cacheTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
  refetchInterval?: number | false;
  refetchOnReconnect?: boolean;
  keepPreviousData?: boolean;
  onError?: (err: ErrorShape) => void;
  onSuccess?: (data: T) => void;
};

function createNoSuperjsonOnlyHookContext<
  T extends { path: string; output: any; input: any },
>(c: T['path']) {
  type Output = NoSuperjsonTransportValue<T['output']>;

  type UseQueryFn = T['input'] extends never
    ? (
        q?: undefined,
        options?: Options<Output>,
      ) => UseTRPCQueryResult<Output, ErrorShape>
    : T['input'] extends Nil
    ? (
        input?: undefined,
        options?: Options<Output>,
      ) => UseTRPCQueryResult<Output, ErrorShape>
    : (
        input: T['input'],
        op?: Options<Output>,
      ) => UseTRPCQueryResult<Output, ErrorShape>;

  type UseInfiniteQuery = (
    input: T['input'],
    opts: UseTRPCInfiniteQueryOptions<any, any, any, any>,
  ) => UseTRPCInfiniteQueryResult<Output, Error>;

  type UseQueryContext = {
    fetch: (input: T['input']) => Promise<Output>;
    refetch: () => Promise<void>;
    invalidate: (input?: T['input']) => Promise<void>;
    getData: () => Output | undefined;
    setData: (u: undefined, data: Output | undefined) => void;
  };

  return {
    useQuery: ((queryInput: any, options?: any) =>
      noSuperjsonTrpcAny[`ns.${c}`].useQuery(
        queryInput,
        options,
      )) as UseQueryFn,
    useInfiniteQuery: ((queryInput, opts) =>
      noSuperjsonTrpcAny[`ns.${c}`].useInfiniteQuery(
        queryInput,
        opts,
      )) as UseInfiniteQuery,
    path: c,
    output: undefined as unknown as Output,
    input: undefined as unknown as T['input'],
    useContext: (() => {
      return noSuperjsonTrpcAny.useContext()[`ns.${c}`];
    }) as () => UseQueryContext,
  };
}

export function createNoSuperjsonOnlyQueryHook<
  T extends { path: string; output: any; input: any },
>(c: T['path']) {
  return createNoSuperjsonOnlyHookContext<T>(c);
}
